import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { authApi } from "@/api";
import { HttpError } from "@/api/http";
import {
  aoTerminarSessaoNoutraAba,
  clearSession,
  getUserAutenticadoEmCache,
  setCurrentUser,
  useSession,
} from "@/api/session";
import { limparBackdropsOrfaos } from "@/utils/limparBackdropsOrfaos";
import { estaOffline, estaEmProducao, temPermissao } from "@/utils/Permissoes";
import { estaNoAppDesktop } from "@/utils/Electron";
import { useRouteLoading } from "@/composables/useRouteLoading";
import { garantirModoInstalacaoCarregado } from "@/config/InfoApp";
import { correspondeAoPrefixo } from "@/utils/RotaAtiva";

/** Prefixo do nome da rota -> código de permissão de Backoffice que a
 * protege (espelha UsuarioService.confirmPermissao/PERMISSOES_MAP no
 * backend). Note que BACGST (stock) e BACGAC (artigos) são independentes:
 * cada módulo tem o seu próprio código, sem sobreposição. */
const PERMISSAO_POR_PREFIXO_ROTA: Array<[string, string]> = [
  ["backoffice-artigos", "BACGAC"],
  ["backoffice-clientes", "BACGCL"],
  ["backoffice-documentos", "BACGDO"],
  ["backoffice-fornecedores", "BACGFO"],
  ["backoffice-tesouraria", "BACGTE"],
  ["backoffice-stock", "BACGST"],
  ["backoffice-relatorios", "BACGRE"],
];

const EntrarLayout = () => import("@/components/layouts/EntrarLayout.vue");
const AuthFormLayout = () =>
  import("@/components/layouts/AuthFormLayout.vue");
const BackOfficeLayout = () =>
  import("@/components/layouts/BackOfficeLayout.vue");
const PosLayout = () => import("@/components/layouts/PosLayout.vue");

const routes: Array<RouteRecordRaw> = [
  { path: "/", redirect: "/entrar" },

  // ---------------------------------------------------------------------
  // Autenticação
  // ---------------------------------------------------------------------
  // Só o login usa o layout com o visual lateral (aside depois do form): as
  // restantes páginas de autenticação (criar conta, recuperar/redefinir
  // senha) têm formulários maiores/diferentes que ficavam apertados nesse
  // layout de duas colunas.
  {
    path: "/",
    component: EntrarLayout,
    children: [
      {
        path: "entrar",
        name: "auth-entrar",
        component: () => import("@/views/auth/EntrarView.vue"),
      },
    ],
  },
  {
    path: "/",
    component: AuthFormLayout,
    children: [
      {
        path: "criar-conta",
        name: "auth-criar-conta",
        component: () => import("@/views/auth/CriarContaView.vue"),
      },
      {
        // Link de indicação de parceiro (ex.: /criar-conta/ABC123).
        path: "criar-conta/:codigoIndicacao",
        name: "auth-criar-conta-indicacao",
        component: () => import("@/views/auth/CriarContaView.vue"),
        props: true,
      },
      {
        path: "esqueceu-senha",
        name: "auth-esqueceu-senha",
        component: () => import("@/views/auth/EsqueceuSenhaView.vue"),
      },
      {
        path: "redefinir-senha",
        name: "auth-redefinir-senha",
        component: () => import("@/views/auth/RedefinirSenhaView.vue"),
      },
    ],
  },

  // ---------------------------------------------------------------------
  // BackOffice
  // ---------------------------------------------------------------------
  {
    path: "/app/backoffice",
    component: BackOfficeLayout,
    children: [
      {
        path: "",
        name: "backoffice-dashboard",
        component: () => import("@/views/backoffice/DashboardView.vue"),
      },
      {
        path: "notificacoes",
        name: "backoffice-notificacoes",
        component: () => import("@/views/backoffice/NotificacoesView.vue"),
      },

      // -- modulos/facturacao/artigos ---------------------------------
      {
        path: "modulos/facturacao/artigos",
        name: "backoffice-artigos",
        component: () =>
          import(
            "@/views/backoffice/modulos/facturacao/artigos/TodosArtigosView.vue"
          ),
      },
      {
        path: "modulos/facturacao/artigos/criar",
        name: "backoffice-artigos-criar",
        component: () =>
          import(
            "@/views/backoffice/modulos/facturacao/artigos/CriarArtigoView.vue"
          ),
      },
      {
        path: "modulos/facturacao/artigos/categorias/criar",
        name: "backoffice-artigos-categorias-criar",
        component: () =>
          import(
            "@/views/backoffice/modulos/facturacao/artigos/CriarCategoriaView.vue"
          ),
      },
      {
        path: "modulos/facturacao/artigos/categorias/:id",
        name: "backoffice-artigos-categorias-detalhes",
        component: () =>
          import(
            "@/views/backoffice/modulos/facturacao/artigos/DetalhesCategoriaView.vue"
          ),
        props: true,
      },
      {
        path: "modulos/facturacao/artigos/:id/editar",
        name: "backoffice-artigos-editar",
        component: () =>
          import(
            "@/views/backoffice/modulos/facturacao/artigos/EditarArtigoView.vue"
          ),
        props: true,
      },
      {
        path: "modulos/facturacao/artigos/:id",
        name: "backoffice-artigos-detalhes",
        component: () =>
          import(
            "@/views/backoffice/modulos/facturacao/artigos/DetalhesArtigoView.vue"
          ),
        props: true,
      },

      // -- modulos/facturacao/clientes ---------------------------------
      {
        path: "modulos/facturacao/clientes",
        name: "backoffice-clientes",
        component: () =>
          import(
            "@/views/backoffice/modulos/facturacao/clientes/TodosClienteView.vue"
          ),
      },
      {
        path: "modulos/facturacao/clientes/criar",
        name: "backoffice-clientes-criar",
        component: () =>
          import(
            "@/views/backoffice/modulos/facturacao/clientes/CriarClienteView.vue"
          ),
      },
      {
        path: "modulos/facturacao/clientes/:id/editar",
        name: "backoffice-clientes-editar",
        component: () =>
          import(
            "@/views/backoffice/modulos/facturacao/clientes/EditarClienteView.vue"
          ),
        props: true,
      },
      {
        path: "modulos/facturacao/clientes/:id",
        name: "backoffice-clientes-detalhes",
        component: () =>
          import(
            "@/views/backoffice/modulos/facturacao/clientes/DetalhesClienteView.vue"
          ),
        props: true,
      },

      // -- modulos/facturacao/documentos --------------------------------
      {
        path: "modulos/facturacao/documentos",
        name: "backoffice-documentos",
        component: () =>
          import(
            "@/views/backoffice/modulos/facturacao/documentos/TodosDocumentosView.vue"
          ),
      },
      {
        path: "modulos/facturacao/documentos/novo",
        name: "backoffice-documentos-novo",
        component: () =>
          import(
            "@/views/backoffice/modulos/facturacao/documentos/NovoDocumentoView.vue"
          ),
      },
      {
        path: "modulos/facturacao/documentos/saft",
        name: "backoffice-documentos-saft",
        component: () =>
          import(
            "@/views/backoffice/modulos/facturacao/documentos/SaftView.vue"
          ),
      },
      {
        path: "modulos/facturacao/documentos/:id/emitir-factura",
        name: "backoffice-documentos-emitir-factura-proposta",
        component: () =>
          import(
            "@/views/backoffice/modulos/facturacao/documentos/EmitirFacturaReferenteAPropostaView.vue"
          ),
        props: true,
      },
      {
        path: "modulos/facturacao/documentos/:id/nota-credito",
        name: "backoffice-documentos-emitir-nota-credito",
        component: () =>
          import(
            "@/views/backoffice/modulos/facturacao/documentos/EmitirNotaDeCreditoView.vue"
          ),
        props: true,
      },
      {
        path: "modulos/facturacao/documentos/:id/emitir-factura-referente-proposta",
        name: "backoffice-documentos-emitir-factura-referente",
        component: () =>
          import(
            "@/views/backoffice/modulos/facturacao/documentos/EmitirFacturaReferenteAPropostaView.vue"
          ),
        props: true,
      },
      {
        path: "modulos/facturacao/documentos/:id",
        name: "backoffice-documentos-detalhes",
        component: () =>
          import(
            "@/views/backoffice/modulos/facturacao/documentos/DetalhesDocumentoView.vue"
          ),
        props: true,
      },

      // -- modulos/facturacao/fornecedores -------------------------------
      {
        path: "modulos/facturacao/fornecedores",
        name: "backoffice-fornecedores",
        component: () =>
          import(
            "@/views/backoffice/modulos/facturacao/fornecedores/TodosFornecedoresView.vue"
          ),
      },
      {
        path: "modulos/facturacao/fornecedores/criar",
        name: "backoffice-fornecedores-criar",
        component: () =>
          import(
            "@/views/backoffice/modulos/facturacao/fornecedores/CriarFornecedorView.vue"
          ),
      },
      {
        path: "modulos/facturacao/fornecedores/:id/editar",
        name: "backoffice-fornecedores-editar",
        component: () =>
          import(
            "@/views/backoffice/modulos/facturacao/fornecedores/EditarFornecedorView.vue"
          ),
        props: true,
      },
      {
        path: "modulos/facturacao/fornecedores/:id",
        name: "backoffice-fornecedores-detalhes",
        component: () =>
          import(
            "@/views/backoffice/modulos/facturacao/fornecedores/DetalhesFornecedorView.vue"
          ),
        props: true,
      },

      // -- modulos/relatorios --------------------------------------------
      {
        path: "modulos/relatorios",
        redirect: () => ({
          name: "backoffice-relatorios",
          params: { ano: String(new Date().getFullYear()) },
        }),
      },
      {
        path: "modulos/relatorios/:ano",
        name: "backoffice-relatorios",
        component: () =>
          import(
            "@/views/backoffice/modulos/relatorios/RelatoriosGeraisView.vue"
          ),
        props: true,
      },

      // -- modulos/stock ---------------------------------------------------
      {
        path: "modulos/stock",
        name: "backoffice-stock",
        component: () =>
          import("@/views/backoffice/modulos/stock/MainStockView.vue"),
      },
      {
        path: "modulos/stock/registar",
        name: "backoffice-stock-registar",
        component: () =>
          import("@/views/backoffice/modulos/stock/RegistarStockView.vue"),
      },

      // -- modulos/tesouraria -------------------------------------------
      {
        path: "modulos/tesouraria",
        name: "backoffice-tesouraria",
        component: () =>
          import(
            "@/views/backoffice/modulos/tesouraria/MainTesourariaView.vue"
          ),
      },
      {
        path: "modulos/tesouraria/:id",
        name: "backoffice-tesouraria-detalhes",
        component: () =>
          import("@/views/backoffice/modulos/tesouraria/DetalhesContaView.vue"),
        props: true,
      },

      // -- configuracoes ----------------------------------------------------
      {
        path: "configuracoes/definicoes-gerais",
        name: "backoffice-config-definicoes-gerais",
        component: () =>
          import("@/views/backoffice/configuracoes/DefinicoesGeraisView.vue"),
      },
      {
        path: "configuracoes/editar-dados-comerciante",
        name: "backoffice-config-editar-dados-comerciante",
        component: () =>
          import(
            "@/views/backoffice/configuracoes/EditarDadosComercianteView.vue"
          ),
      },
      {
        path: "configuracoes/integracao-api",
        name: "backoffice-config-integracao-api",
        component: () =>
          import("@/views/backoffice/configuracoes/IntegracaoApiView.vue"),
      },
      {
        path: "configuracoes/configurar-chaves-facturacao-electronica",
        name: "backoffice-config-chaves-facturacao-electronica",
        component: () =>
          import(
            "@/views/backoffice/configuracoes/ConfigurarChavesFacturacaoElectronicaView.vue"
          ),
      },
      {
        path: "configuracoes/caixas/criar",
        name: "backoffice-config-caixas-criar",
        component: () =>
          import("@/views/backoffice/configuracoes/caixas/CriarCaixaView.vue"),
      },
      {
        path: "configuracoes/caixas/:id/editar",
        name: "backoffice-config-caixas-editar",
        component: () =>
          import("@/views/backoffice/configuracoes/caixas/EditarCaixaView.vue"),
        props: true,
      },
      {
        path: "configuracoes/caixas/:id",
        name: "backoffice-config-caixas-detalhes",
        component: () =>
          import(
            "@/views/backoffice/configuracoes/caixas/DetalhesCaixaView.vue"
          ),
        props: true,
      },
      {
        path: "configuracoes/conta-bancarias",
        name: "backoffice-config-contas-bancarias",
        component: () =>
          import(
            "@/views/backoffice/configuracoes/conta-bancarias/TodasContasBancariasView.vue"
          ),
      },
      {
        path: "configuracoes/conta-bancarias/criar",
        name: "backoffice-config-contas-bancarias-criar",
        component: () =>
          import(
            "@/views/backoffice/configuracoes/conta-bancarias/CriarContaBancariaView.vue"
          ),
      },
      {
        path: "configuracoes/conta-bancarias/:id/editar",
        name: "backoffice-config-contas-bancarias-editar",
        component: () =>
          import(
            "@/views/backoffice/configuracoes/conta-bancarias/EditarContaBancariaView.vue"
          ),
        props: true,
      },
      {
        path: "configuracoes/conta-bancarias/:id",
        name: "backoffice-config-contas-bancarias-detalhes",
        component: () =>
          import(
            "@/views/backoffice/configuracoes/conta-bancarias/DetalhesContaView.vue"
          ),
        props: true,
      },
      {
        path: "configuracoes/estabelecimentos/criar",
        name: "backoffice-config-estabelecimentos-criar",
        component: () =>
          import(
            "@/views/backoffice/configuracoes/estabelecimentos/CriarEstabelecimentoView.vue"
          ),
      },
      {
        path: "configuracoes/estabelecimentos/:id/editar",
        name: "backoffice-config-estabelecimentos-editar",
        component: () =>
          import(
            "@/views/backoffice/configuracoes/estabelecimentos/EditarEstabelecimentoView.vue"
          ),
        props: true,
      },
      {
        path: "configuracoes/estabelecimentos/:id",
        name: "backoffice-config-estabelecimentos-detalhes",
        component: () =>
          import(
            "@/views/backoffice/configuracoes/estabelecimentos/DetalhesEstabelecimentoView.vue"
          ),
        props: true,
      },
      {
        path: "configuracoes/mesas/criar",
        name: "backoffice-config-mesas-criar",
        component: () =>
          import("@/views/backoffice/configuracoes/mesas/CriarMesaView.vue"),
      },
      {
        path: "configuracoes/mesas/:id/editar",
        name: "backoffice-config-mesas-editar",
        component: () =>
          import("@/views/backoffice/configuracoes/mesas/EditarMesaView.vue"),
        props: true,
      },
      {
        path: "configuracoes/mesas/:id",
        name: "backoffice-config-mesas-detalhes",
        component: () =>
          import("@/views/backoffice/configuracoes/mesas/DetalhesMesaView.vue"),
        props: true,
      },
      {
        path: "configuracoes/utilizadores",
        name: "backoffice-config-utilizadores",
        component: () =>
          import(
            "@/views/backoffice/configuracoes/utilizadores/TodosUtilizadoresView.vue"
          ),
      },
      {
        path: "configuracoes/utilizadores/criar",
        name: "backoffice-config-utilizadores-criar",
        component: () =>
          import(
            "@/views/backoffice/configuracoes/utilizadores/CriarUtilizadorView.vue"
          ),
      },
      {
        path: "configuracoes/utilizadores/:id",
        name: "backoffice-config-utilizadores-detalhes",
        component: () =>
          import(
            "@/views/backoffice/configuracoes/utilizadores/DetalhesUtilizadorView.vue"
          ),
        props: true,
      },
      {
        path: "configuracoes/utilizadores/:id/editar",
        name: "backoffice-config-utilizadores-editar",
        component: () =>
          import(
            "@/views/backoffice/configuracoes/utilizadores/EditarUtilizadorView.vue"
          ),
        props: true,
      },

      // -- licenca --------------------------------------------------------
      {
        path: "licenca/assinatura",
        name: "backoffice-licenca-assinatura",
        component: () =>
          import("@/views/backoffice/licenca/AssinaturaView.vue"),
      },
      {
        path: "licenca/pagar-por-express",
        name: "backoffice-licenca-pagar-por-express",
        component: () =>
          import("@/views/backoffice/licenca/PagarPorExpressView.vue"),
      },
    ],
  },

  // ---------------------------------------------------------------------
  // POS
  // ---------------------------------------------------------------------
  {
    path: "/app/pos",
    component: PosLayout,
    children: [
      {
        path: "terminal",
        name: "pos-terminal",
        component: () => import("@/views/pos/terminal/PdvView.vue"),
      },
      {
        path: "terminal/restaurante",
        name: "pos-terminal-restaurante",
        component: () => import("@/views/pos/terminal/PdvRestauranteView.vue"),
      },
      {
        path: "abertura-caixa",
        name: "pos-abertura-caixa",
        component: () => import("@/views/pos/AberturaCaixaView.vue"),
      },
      {
        path: "fecho-caixa",
        name: "pos-fecho-caixa",
        component: () => import("@/views/pos/FechoCaixaView.vue"),
      },
      {
        path: "historico-caixas",
        name: "pos-historico-caixas",
        component: () => import("@/views/pos/HistoricoCaixasView.vue"),
      },
      {
        path: "minhas-vendas",
        name: "pos-minhas-vendas",
        component: () => import("@/views/pos/MinhasVendasView.vue"),
      },
      {
        path: "retirada-cash",
        name: "pos-retirada-cash",
        component: () => import("@/views/pos/RetiradaCashView.vue"),
      },
      {
        path: "emitir-documento",
        name: "pos-emitir-documento",
        component: () => import("@/views/pos/EmitirDocumentoView.vue"),
      },
      {
        path: "emitir-recibo",
        name: "pos-emitir-recibo",
        component: () => import("@/views/pos/EmitirReciboView.vue"),
      },
      {
        path: "emitir-nota-credito",
        name: "pos-emitir-nota-credito",
        component: () => import("@/views/pos/EmitirNotaCreditoView.vue"),
      },
    ],
  },
  // Tela de cliente is a standalone kiosk display (opened in its own tab from
  // the terminal), so it does not carry the PosLayout topbar/user menu chrome.
  {
    path: "/app/pos/terminal/tela-cliente",
    name: "pos-terminal-tela-cliente",
    component: () => import("@/views/pos/terminal/TelaClienteView.vue"),
  },

  // ---------------------------------------------------------------------
  {
    path: "/servico-indisponivel",
    name: "servico-indisponivel",
    component: () => import("@/views/ServicoIndisponivelView.vue"),
  },
  {
    path: "/app-desktop-necessario",
    name: "app-desktop-necessario",
    component: () => import("@/views/AppDesktopNecessarioView.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () => import("@/views/NotFoundView.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  linkActiveClass: "active",
  linkExactActiveClass: "active",
  scrollBehavior() {
    return { top: 0 };
  },
});

aoTerminarSessaoNoutraAba(() => {
  const rotaActual = router.currentRoute.value;
  const protegida =
    rotaActual.path.startsWith("/app/backoffice") ||
    rotaActual.path.startsWith("/app/pos");
  if (protegida) router.push({ name: "auth-entrar" });
});

const { iniciar: iniciarCargaRota, concluir: concluirCargaRota } =
  useRouteLoading();
router.beforeEach((to, from) => {
  if (to.path !== from.path) iniciarCargaRota();
  return true;
});

/** Nenhuma rota deve ficar acessível sem primeiro sabermos o modo de
 * instalação (ONLINE/OFFLINE) — este valor vem sempre da API, nunca é
 * assumido no Vue. Se a API não responder, bloqueia a navegação e mostra
 * uma mensagem genérica em vez de expor o motivo técnico. */
router.beforeEach(async (to) => {
  if (to.name === "servico-indisponivel") return true;
  const carregouComSucesso = await garantirModoInstalacaoCarregado();
  if (!carregouComSucesso) {
    return { name: "servico-indisponivel", query: { destino: to.fullPath } };
  }
  return true;
});

/** Uma instalação OFFLINE só deve ser usada através do aplicativo Desktop
 * Ulemo (Electron) — nunca directamente por um navegador comum. */
router.beforeEach((to) => {
  if (to.name === "app-desktop-necessario") return true;
  if (estaOffline() && !estaNoAppDesktop()) {
    return { name: "app-desktop-necessario" };
  }
  return true;
});

router.beforeEach(async (to) => {
  const protegida =
    to.path.startsWith("/app/backoffice") || to.path.startsWith("/app/pos");
  const paginaAuth = typeof to.name === "string" && to.name.startsWith("auth-");
  if (!protegida && !paginaAuth) return true;

  if (
    (to.name === "auth-criar-conta" ||
      to.name === "auth-criar-conta-indicacao") &&
    (estaOffline() || !estaEmProducao())
  ) {
    return { name: "auth-entrar" };
  }

  try {
    let userAuth = getUserAutenticadoEmCache();
    if (!userAuth) {
      try {
        const resposta = await authApi.usuarioAutenticado();
        userAuth = resposta.userAuth;
        if (userAuth) setCurrentUser(userAuth);
      } catch (erroPedido) {
        // Falha de transporte (rede instável, timeout) não é o servidor a
        // dizer que a sessão é inválida — só um HttpError (ex.: 401) é. Usa
        // os dados em cache em vez de forçar logout por instabilidade.
        if (erroPedido instanceof HttpError) throw erroPedido;
        userAuth = useSession().currentUser.value;
      }
    }

    if (userAuth) {
      if (paginaAuth) return { name: "backoffice-dashboard" };

      const assinatura = userAuth.comerciante.assinatura;
      const diasRestantes = assinatura?.diasRestantes ?? 0;
      const paginaLicenca =
        to.name === "backoffice-licenca-assinatura" ||
        to.name === "backoffice-licenca-pagar-por-express";
      if (diasRestantes <= 0 && !paginaLicenca) {
        return { name: "backoffice-licenca-assinatura" };
      }

      const semAcesso =
        assinatura?.plano === "Facturacao" &&
        (to.path.startsWith("/app/pos") ||
          (typeof to.name === "string" &&
            to.name.startsWith("backoffice-stock")));
      if (semAcesso) {
        return { name: "backoffice-dashboard" };
      }

      const ROTAS_INDISPONIVEIS_OFFLINE = [
        "backoffice-config-integracao-api",
        "backoffice-config-chaves-facturacao-electronica",
        "backoffice-licenca-pagar-por-express",
      ];
      if (
        estaOffline() &&
        ROTAS_INDISPONIVEIS_OFFLINE.includes(to.name as string)
      ) {
        return { name: "backoffice-dashboard" };
      }

      // Chaves de API e de facturação electrónica são segredos do comerciante:
      // só ADMIN/SADMIN têm acesso, mesmo que o OPERADOR tenha permissão geral
      // de configurações (o backend também já não envia estas chaves a
      // OPERADOR — isto é só para não mostrar uma página em branco/confusa).
      const ROTAS_APENAS_ADMIN = [
        "backoffice-config-integracao-api",
        "backoffice-config-chaves-facturacao-electronica",
      ];
      if (
        userAuth.grupo === "OPERADOR" &&
        ROTAS_APENAS_ADMIN.includes(to.name as string)
      ) {
        return { name: "backoffice-dashboard" };
      }

      if (userAuth.grupo === "OPERADOR") {
        const temBackoffice =
          userAuth.minhasPermissoes.backoffice.includes("BACBOF");
        const temPos = userAuth.minhasPermissoes.pos.includes("POSPDV");
        if (to.path.startsWith("/app/backoffice") && !temBackoffice && temPos) {
          return { name: "pos-terminal" };
        }
        if (to.path.startsWith("/app/pos") && !temPos && temBackoffice) {
          return { name: "backoffice-dashboard" };
        }
      }

      if (typeof to.name === "string") {
        const ehAdmin =
          userAuth.grupo === "ADMIN" || userAuth.grupo === "SADMIN";
        const ehMeuPerfil =
          to.name === "backoffice-config-utilizadores-detalhes" &&
          to.params.id === "eu";
        if (
          correspondeAoPrefixo(to.name, "backoffice-config") &&
          !ehAdmin &&
          !ehMeuPerfil
        ) {
          return { name: "backoffice-dashboard" };
        }
        const entradaPermissao = PERMISSAO_POR_PREFIXO_ROTA.find(([prefixo]) =>
          correspondeAoPrefixo(to.name as string, prefixo),
        );
        if (
          entradaPermissao &&
          !temPermissao(entradaPermissao[1], "Backoffice")
        ) {
          return { name: "backoffice-dashboard" };
        }
      }

      if (to.path.startsWith("/app/pos") && typeof to.name === "string") {
        const sessaoPOS = userAuth.sessaoPOS;
        const nomeRotaTerminal =
          sessaoPOS?.terminal === "Restaurante"
            ? "pos-terminal-restaurante"
            : "pos-terminal";

        if (to.name === "pos-abertura-caixa" && sessaoPOS) {
          return { name: nomeRotaTerminal };
        }

        const ROTAS_EXIGEM_SESSAO_POS = [
          "pos-terminal",
          "pos-terminal-restaurante",
          "pos-fecho-caixa",
          "pos-retirada-cash",
          "pos-emitir-documento",
          "pos-emitir-recibo",
          "pos-emitir-nota-credito",
        ];
        if (ROTAS_EXIGEM_SESSAO_POS.includes(to.name) && !sessaoPOS) {
          return { name: "pos-abertura-caixa" };
        }

        if (
          sessaoPOS &&
          (to.name === "pos-terminal" ||
            to.name === "pos-terminal-restaurante") &&
          to.name !== nomeRotaTerminal
        ) {
          return { name: nomeRotaTerminal };
        }
      }

      return true;
    }
    clearSession();
    if (protegida) {
      await authApi.logout();
      return { name: "auth-entrar" };
    }
    return true;
  } catch {
    clearSession();
    return protegida ? { name: "auth-entrar" } : true;
  }
});

router.afterEach(() => {
  limparBackdropsOrfaos();
  concluirCargaRota();
});

router.onError(() => {
  concluirCargaRota();
});

export default router;
